import React, { useEffect, useState } from "react";
import { PageHero } from "@/components/PageParts";
import { Gift, Heart } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function GiftLegacy() {
  const [amounts, setAmounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", recipient_name: "", recipient_email: "", personal_message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/content/voucher-amounts").then((r) => {
      setAmounts(r.data || []);
      if ((r.data || []).length && !selected) setSelected(r.data[1]?.id || r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) { toast.error("Please choose an amount"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        origin_url: window.location.origin,
        voucher_amount_id: selected,
        ...form,
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-gift">
      <PageHero
        eyebrow="Gift a Legacy"
        title="The gift they will pass on to their children."
        subtitle="Give a beautifully-presented voucher for a legacy film. They choose when they're ready — we take care of everything else."
        image="https://images.pexels.com/photos/8848784/pexels-photo-8848784.jpeg"
        testid="gift-hero"
      />

      <section className="section-pad bg-cream">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12">
          <div>
            <Gift size={30} style={{ color: "#C9A961" }} className="mb-5" />
            <h2 className="font-serif text-4xl mb-5">A meaningful gift, thoughtfully given.</h2>
            <p className="text-warmgrey text-lg mb-6 leading-relaxed">
              Vouchers are emailed instantly as a beautifully-designed printable certificate — perfect for wrapping, tucking into a card, or placing under the Christmas tree.
            </p>
            <ul className="space-y-3 text-warmgrey">
              <li className="flex gap-3"><Heart size={18} style={{ color: "#C9A961" }} className="mt-1" /> Elegant printable PDF certificate</li>
              <li className="flex gap-3"><Heart size={18} style={{ color: "#C9A961" }} className="mt-1" /> Redeemable within 24 months</li>
              <li className="flex gap-3"><Heart size={18} style={{ color: "#C9A961" }} className="mt-1" /> Applies toward any package we offer</li>
              <li className="flex gap-3"><Heart size={18} style={{ color: "#C9A961" }} className="mt-1" /> Transferable to another family member</li>
            </ul>
          </div>

          <form onSubmit={submit} className="bg-white p-8 rounded-xl border border-border-warm" data-testid="gift-form">
            <p className="eyebrow mb-4">Choose an amount</p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {amounts.map((a) => (
                <button type="button" key={a.id} onClick={() => setSelected(a.id)}
                  data-testid={`voucher-amount-${a.id}`}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${selected === a.id ? "border-primary bg-primary/5" : "border-border-warm hover:border-primary"}`}>
                  <div className="font-serif text-2xl">${Math.round(a.amount_aud).toLocaleString()}</div>
                  <div className="text-xs text-warmgrey mt-1">{a.label.split("—")[0].trim()}</div>
                </button>
              ))}
            </div>
            <p className="eyebrow mb-4">Your details</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input required data-testid="gift-buyer-name" className="input-field" placeholder="Your name" value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} />
              <input required data-testid="gift-buyer-email" type="email" className="input-field" placeholder="Your email" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} />
            </div>
            <p className="eyebrow mb-4 mt-6">Recipient</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input data-testid="gift-recipient-name" className="input-field" placeholder="Recipient name" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} />
              <input data-testid="gift-recipient-email" type="email" className="input-field" placeholder="Recipient email (optional)" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} />
            </div>
            <textarea data-testid="gift-message" className="input-field mt-3 min-h-[100px]" placeholder="A personal note (optional)" value={form.personal_message} onChange={(e) => setForm({ ...form, personal_message: e.target.value })} />
            <button disabled={loading} className="btn-primary w-full justify-center mt-6" data-testid="gift-submit">
              {loading ? "Redirecting..." : "Continue to Secure Checkout"}
            </button>
            <p className="text-xs text-warmgrey-muted text-center mt-4">Powered by Stripe — safe, secure, and instant delivery.</p>
          </form>
        </div>
      </section>
    </div>
  );
}
